import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { Row, Col, Button, Card, CardBody, Spinner, Label } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import styles from "../../MainPrintListTabs/css/printlabel.module.scss";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import tableStyles from "../../../../assets/scss/common/table.module.scss";
import sf from "../../../../assets/scss/common/searchFilter.module.scss";
import SimpleBar from "simplebar-react";
import { templateApiService } from "modules/Library/Template/api/template.api";
import DOMPurify from "dompurify";
import Barcode from "react-barcode";
import { createRoot } from "react-dom/client";
import { useQueryClient } from "@tanstack/react-query";
import { showToastMessage } from "common/service/CommonService";
import { useStudyTimepoints } from "hooks/useStudyTimepoints";
import { useGlobalStudy } from "../../../../context/StudyContext";
import { CryovialLabelFormValues } from "../helper/rialabel.types";
import { rialabelColumns, rialabelFields } from "../helper/rialabel.constant";
import { useRiaLabelService } from "../api/useRiaLabelService";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import CommonButton from "Components/Common/CommonButton";
import {
  customSelectComponents,
  getCustomSelectStyles,
  handleAllMultiSelectChange,
} from "../../MainPrintListTabs/helper/reportSelectUtils";

const CryovialLabelList: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [templateHtml, setTemplateHtml] = useState<string | null>(null);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [individualCounts, setIndividualCounts] = useState<{
    [key: number]: number;
  }>({});
  const previewRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const getCountForId = (id: number) => individualCounts[id] || 1;

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CryovialLabelFormValues>({
    defaultValues: {
      studyid: null,
      period: null,
      timepoint: ["ALL"],
      aliquot: null,
    },
  });

  const selectedStudyId = watch("studyid");
  const selectedPeriod = watch("period");
  const selectedTimepoint = watch("timepoint");
  const selectedAliquot = watch("aliquot");

  const { selectedStudy } = useGlobalStudy();

  // Fetch periods, timepoints
  const { dynamicPeriods, dynamicPhases } = useStudyTimepoints(
    Number(selectedStudyId),
  );

  const periodOptions = useMemo(() => {
    return dynamicPeriods.map((p: any) => ({
      label: String(p.periodorder).padStart(2, "0"),
      value: String(p.periodorder).padStart(2, "0"),
    }));
  }, [dynamicPeriods]);

  // Fetch study details directly to get number of aliquots (avoids touching shared hook)
  const { SelectQuery: studyDetailQuery } = useMasterStudyService({
    id: Number(selectedStudyId),
    enabledSelect: true
  });

  // Fetch all master aliquot records
  const aliquotOptions = useMemo(() => {
    const masterAliquote =
      (studyDetailQuery as any)?.data?.data?.[0]?.aliquots || [];

    // De-duplicate aliquots by aliquot_number
    const uniqueAliquots = Array.from(
      new Map(
        masterAliquote.map((item: any) => [item.aliquot_number, item]),
      ).values(),
    );

    const opts = uniqueAliquots.map((item: any) => ({
      label: String(item.aliquot_number),
      value: String(item.aliquot_number),
    }));
    return [{ label: "All Aliquots", value: "ALL" }, ...opts];
  }, [studyDetailQuery?.data?.data]);

  useEffect(() => {
    if (selectedStudy) {
      setValue("studyid", Number(selectedStudy.studyid));
    }
  }, [selectedStudy, setValue]);

  // Reset fields when study changes
  useEffect(() => {
    setValue("period", periodOptions[0]?.value || null);
    setValue("timepoint", ["ALL"]);
    setValue("aliquot", ["ALL"]);
  }, [selectedStudyId, setValue, periodOptions, aliquotOptions]);

  // Fetch all studies for the dropdown
  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({ enabledDropDown: true });

  const studyOptions =
    (studySelectAllQuery.data as any)?.data?.map((study: any) => ({
      label: study.studycode,
      value: study.studyid,
    })) || [];

  const selectedStudyLabel =
    studyOptions.find((opt: any) => opt.value === selectedStudyId)?.label || "";

  const timepointOptions = useMemo(() => {
    const opts = dynamicPhases.map((tp: any) => ({
      label: tp.label,
      value: tp.label,
    }));
    return [{ label: "All Timepoints", value: "ALL" }, ...opts];
  }, [dynamicPhases]);

  useEffect(() => {
    if (!selectedPeriod && periodOptions.length > 0) {
      setValue("period", periodOptions[0].value);
    }
  }, [selectedPeriod, periodOptions, setValue]);

  useEffect(() => {
    if (!selectedTimepoint || (Array.isArray(selectedTimepoint) && selectedTimepoint.length === 0)) {
      setValue("timepoint", ["ALL"]);
    }
  }, [selectedTimepoint, setValue]);

  useEffect(() => {
    if (!selectedAliquot || (Array.isArray(selectedAliquot) && selectedAliquot.length === 0)) {
      setValue("aliquot", ["ALL"]);
    }
  }, [selectedAliquot, setValue]);

  const { SelectAllQuery: sampleSeparationQuery, UpdateCryovialLabelMutation } =
    useRiaLabelService({
      studyid: selectedStudyId ? Number(selectedStudyId) : null,
      period: selectedPeriod,
      timepoint: selectedTimepoint,
      aliquot: selectedAliquot,
      searchtext: searchText,
    });

  const apiResponse = sampleSeparationQuery.data as any;
  const rawTableData: any[] = useMemo(
    () => apiResponse?.data || [],
    [apiResponse?.data]
  );

  const tableData = useMemo(() => {
    return rawTableData.filter((row: any) => {
      // Period filter
      if (selectedPeriod && selectedPeriod !== "ALL") {
        const rowPeriod = String(row.period_number || "").padStart(2, "0");
        const targetPeriod = String(selectedPeriod).padStart(2, "0");
        if (rowPeriod !== targetPeriod) return false;
      }

      // Timepoint filter
      if (selectedTimepoint) {
        if (Array.isArray(selectedTimepoint)) {
          if (!selectedTimepoint.includes("ALL")) {
            const rowTp = String(row.timepoint_number || "").trim();
            const isMatch = selectedTimepoint.some(
              (tp: any) =>
                String(tp).trim() === rowTp ||
                Number(tp) === Number(rowTp)
            );
            if (!isMatch) return false;
          }
        } else if (selectedTimepoint !== "ALL") {
          const rowTp = String(row.timepoint_number || "").trim();
          const targetTp = String(selectedTimepoint).trim();
          if (rowTp !== targetTp && Number(rowTp) !== Number(targetTp)) {
            return false;
          }
        }
      }

      // Aliquot filter
      if (selectedAliquot) {
        if (Array.isArray(selectedAliquot)) {
          if (!selectedAliquot.includes("ALL")) {
            const rowAli = String(row.aliquot_number || "").trim();
            const isMatch = selectedAliquot.some(
              (ali: any) =>
                String(ali).trim() === rowAli ||
                Number(ali) === Number(rowAli)
            );
            if (!isMatch) return false;
          }
        } else if (selectedAliquot !== "ALL") {
          const rowAli = String(row.aliquot_number || "").trim();
          const targetAli = String(selectedAliquot).trim();
          if (rowAli !== targetAli && Number(rowAli) !== Number(targetAli)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [rawTableData, selectedPeriod, selectedTimepoint, selectedAliquot]);

  const totalRecords = tableData.length;

  const isFilterValid = Boolean(
    selectedStudyId &&
    selectedPeriod &&
    selectedTimepoint &&
    (Array.isArray(selectedTimepoint) ? selectedTimepoint.length > 0 : !!selectedTimepoint) &&
    selectedAliquot &&
    (Array.isArray(selectedAliquot) ? selectedAliquot.length > 0 : !!selectedAliquot)
  );

  useEffect(() => {
    setSelectedRows([]);
    setCurrentIndex(0);
  }, [
    selectedStudyId,
    selectedPeriod,
    selectedTimepoint,
    selectedAliquot,
    searchText,
  ]);

  // Fetch Cryovial Template
  useEffect(() => {
    if (selectedStudyId) {
      fetchTemplate();
    }
  }, [selectedStudyId]);

  const fetchTemplate = async () => {
    try {
      setIsTemplateLoading(true);
      const res = await templateApiService.getSelectAllData({
        searchtext: "Cyrovial Label",
        pagenumber: 1,
        pagesize: 10,
        status: "A",
      });
      const templates = res?.data || [];
      const labelTemplate = templates.find((t: any) => {
        const name = String(t.templatename || t.name || "").toLowerCase();
        return name.includes("cyrovial") || name.includes("cyrovial label");
      });
      if (labelTemplate) {
        setTemplateHtml(labelTemplate.designtemplate);
      } else if (templates.length > 0) {
        setTemplateHtml(templates[0].designtemplate);
      }
    } catch (error) {
      console.error("Error fetching template", error);
    } finally {
      setIsTemplateLoading(false);
    }
  };

  const selectedSeparationIds = useMemo(
    () => selectedRows.map((row) => row.sampleseparationid),
    [selectedRows],
  );

  const isAllSelected = useMemo(() => {
    return (
      tableData.length > 0 &&
      tableData.every((row: any) =>
        selectedSeparationIds.includes(row.sampleseparationid),
      )
    );
  }, [tableData, selectedSeparationIds]);

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows(tableData);
    } else {
      setSelectedRows([]);
    }
    setCurrentIndex(0);
  };

  const toggleSelection = (row: any, isChecked: boolean) => {
    setSelectedRows((prev) =>
      isChecked
        ? [...prev, row]
        : prev.filter((r) => r.sampleseparationid !== row.sampleseparationid),
    );
    setCurrentIndex(0);
  };

  const mapSubjectToData = (row: any) => {
    const barcodeValue = row.rialbarcode;
    const barcodeHtml = `<div class="barcode-target" data-barcode="${barcodeValue}"></div>`;
    return {
      SubjectNo: row.subjectno || "N/A",
      Initials: row.initials || "N/A",
      StudyCode: selectedStudyLabel || "N/A",
      Period: row.period_number || "N/A",
      Timepoint: row.timepoint_number || "N/A",
      AliquotNumber: row.aliquot_number || "1",
      Barcode: barcodeHtml,
    };
  };

  const replacePlaceholders = (template: string, data: Record<string, any>) => {
    return template.replace(/{{(.*?)}}/g, (_, key) => {
      return data[key.trim()] ?? "";
    });
  };

  const previewList = selectedRows.length > 0 ? selectedRows : [];
  const currentSubject = previewList[currentIndex];

  const mappedPreviewHtml = useMemo(() => {
    if (!templateHtml || previewList.length === 0) return null;
    const currentData = previewList[currentIndex] || previewList[0];
    if (!currentData) return null;
    const data = mapSubjectToData(currentData);
    return replacePlaceholders(templateHtml, data);
  }, [templateHtml, previewList, currentIndex, selectedStudyLabel]);

  // Barcode Rendering Effect - useLayoutEffect for zero-blink rendering

  useLayoutEffect(() => {
    if (previewRef.current && mappedPreviewHtml) {
      const container = previewRef.current;
      const targets = container.querySelectorAll(".barcode-target");
      targets.forEach((target) => {
        const barcodeVal = target.getAttribute("data-barcode");
        if (barcodeVal && target.children.length === 0) {
          const root = createRoot(target as HTMLElement);
          root.render(
            <Barcode
              value={barcodeVal}
              format="CODE128"
              width={1.5}
              height={55}
              displayValue={false}
              margin={0}
            />,
          );
        }
      });
    }
  }, [
    mappedPreviewHtml,
    selectedRows,
    individualCounts,
    sampleSeparationQuery.isFetching,
    UpdateCryovialLabelMutation.isPending,
  ]);

  const handlePrint = () => {
    if (selectedRows.length === 0) return;

    const payload = selectedRows
      .map((row) => {
        const id = row.sampleseparationid;
        if (id === undefined || id === null) return null;
        return { id, quantity: getCountForId(id) };
      })
      .filter(Boolean);

    UpdateCryovialLabelMutation.mutate(
      {
        sampleseparation: payload,
        studyid: selectedStudyId ? Number(selectedStudyId) : null,
      },
      {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res.message || "Cryovial Label record updated successfully!",
          );
          queryClient.invalidateQueries({
            queryKey: ["sample-separation-SelectAll"],
          });

          // Reset selection and count states
          setSelectedRows([]);
          setIndividualCounts({});
        },
        onError: (err: any) => {
          showToastMessage(
            "error",
            err.message || "Error updating print record",
          );
        },
      },
    );
  };
  const { hasPermission } = usePermission();

  return (
    <Row>
      {/* Left Column - Form & Table */}
      <Col lg={7}>
        <div className="mb-4">
          <Row className="gy-3 form_input align-items-start">
            <Col md={3}>
              <CommonForm
                fields={rialabelFields(
                  studyOptions,
                  periodOptions,
                  timepointOptions,
                  aliquotOptions,
                  selectedStudyId,
                  selectedPeriod,
                  selectedTimepoint
                ).slice(0, 1)}
                control={control}
                errors={errors}
                columns={1}
              />
            </Col>
            <Col md={3} className="mb-2 position-relative">
              <Label className="mb-2">
                Period <span className="text-danger">*</span>
              </Label>
              <Controller
                name="period"
                control={control}
                render={({ field: controllerField }) => {
                  const isAll = controllerField.value === "ALL";
                  const selectedOpt = periodOptions.find((opt: any) => opt.value === controllerField.value) || null;

                  return (
                    <Select
                      options={periodOptions}
                      value={selectedOpt}
                      isDisabled={!selectedStudyId}
                      onChange={(opt: any) => {
                        setValue("period", opt ? opt.value : "ALL", { shouldValidate: true, shouldDirty: true });
                      }}
                      placeholder="Select Period"
                      menuPortalTarget={document.body}
                      className="common-select"
                      classNamePrefix="common-select"
                      styles={getCustomSelectStyles(!!errors?.period, isAll)}
                    />
                  );
                }}
              />
            </Col>
            <Col md={3} className="mb-2 position-relative">
              <Label className="mb-2">
                Time Point <span className="text-danger">*</span>
              </Label>
              <Controller
                name="timepoint"
                control={control}
                render={({ field: controllerField }) => {
                  const currentVals = Array.isArray(controllerField.value)
                    ? controllerField.value
                    : controllerField.value
                      ? [controllerField.value]
                      : ["ALL"];
                  const selectedOpts = timepointOptions.filter((opt) =>
                    currentVals.includes(opt.value)
                  );
                  const isAll = currentVals.includes("ALL");

                  return (
                    <Select
                      isMulti
                      options={timepointOptions}
                      value={selectedOpts}
                      isDisabled={!selectedStudyId}
                      onChange={(selected: any) =>
                        handleAllMultiSelectChange("timepoint", selected, currentVals, setValue)
                      }
                      placeholder="Select Timepoint"
                      menuPortalTarget={document.body}
                      className="common-select"
                      classNamePrefix="common-select"
                      components={customSelectComponents}
                      styles={getCustomSelectStyles(!!errors?.timepoint, isAll)}
                    />
                  );
                }}
              />
            </Col>
            <Col md={3} className="mb-2 position-relative">
              <Label className="mb-2">
                Aliquot Number <span className="text-danger">*</span>
              </Label>
              <Controller
                name="aliquot"
                control={control}
                render={({ field: controllerField }) => {
                  const currentVals = Array.isArray(controllerField.value)
                    ? controllerField.value
                    : controllerField.value ? [controllerField.value] : ["ALL"];
                  const isAll = currentVals.includes("ALL");
                  const selectedOpts = aliquotOptions.filter((opt: any) =>
                    currentVals.includes(opt.value)
                  );

                  return (
                    <Select
                      isMulti
                      options={aliquotOptions}
                      value={selectedOpts}
                      isDisabled={!selectedStudyId}
                      onChange={(selected: any) =>
                        handleAllMultiSelectChange("aliquot", selected, currentVals, setValue)
                      }
                      placeholder="Select Aliquot Number(s)"
                      menuPortalTarget={document.body}
                      className="common-select"
                      classNamePrefix="common-select"
                      components={customSelectComponents}
                      styles={getCustomSelectStyles(!!errors?.aliquot, isAll)}
                    />
                  );
                }}
              />
            </Col>
          </Row>
        </div>

        {isFilterValid && (
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6
                className="fw-700 mb-0 small text-uppercase ls-1"
                style={{ color: "#008080" }}
              >
                Sample Separation Details
              </h6>
              <div
                className={`${sf.searchBox} ${searchFocused ? sf.focused : ""}`}
                style={{ width: "250px", marginBottom: 0 }}
              >
                <i className={`ri-search-line ${sf.searchIcon}`} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchText}
                  className={sf.searchInput}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                />
                {searchText && (
                  <i
                    className={`ri-close-line ${sf.clearIcon}`}
                    onClick={() => setSearchText("")}
                  />
                )}
              </div>
            </div>
            <CommonTableIndexWise
              columns={rialabelColumns(
                selectedSeparationIds,
                toggleSelection,
                isAllSelected,
                handleSelectAll
              )}
              data={tableData}
              className={tableStyles.table}
              hideAction={true}
              maxHeight="400px"
              stickyHeader={true}
              loading={
                sampleSeparationQuery.isLoading ||
                sampleSeparationQuery.isFetching
              }
              totalRecords={totalRecords}
              paginationShow={false}
            />
          </div>
        )}
      </Col>

      {/* Right Column - Preview */}
      {isFilterValid && (
        <Col lg={5}>
          <Card className="border shadow-none rounded-4 overflow-hidden">
            <CardBody className="p-3 d-flex flex-column">
              <div className="mb-3">
                <h6 className="fw-700 mb-1">Label Preview</h6>
                <p className="text-muted extra-small mb-0">
                  Cryovial Label Preview
                </p>
              </div>

              <div className={styles.preview_container} ref={previewRef}>
                {isTemplateLoading ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" color="primary" />
                  </div>
                ) : currentSubject ? (
                  <div
                    className={styles.simulated_label_wrapper}
                    dangerouslySetInnerHTML={{
                      __html: mappedPreviewHtml || "",
                    }}
                  />
                ) : (
                  <div className="text-center text-muted p-4 border rounded-3 bg-white w-100">
                    <i className="ri-flask-line fs-1 d-block mb-2 opacity-25" />
                    <p className="small mb-0">
                      Select a subject from the list to preview their label
                    </p>
                  </div>
                )}
              </div>

              {previewList.length > 1 && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Button
                      color="light"
                      size="sm"
                      className="rounded-3 p-0 d-flex align-items-center justify-content-center border"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() =>
                        setCurrentIndex(Math.max(0, currentIndex - 1))
                      }
                      disabled={currentIndex === 0}
                    >
                      <i className="ri-arrow-left-s-line fs-5" />
                    </Button>

                    <div className="px-3 py-1 bg-light rounded-3">
                      <span className="fw-700 small text-primary">
                        {currentIndex + 1} of {previewList.length}
                      </span>
                    </div>

                    <Button
                      color="light"
                      size="sm"
                      className="rounded-3 p-0 d-flex align-items-center justify-content-center border"
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() =>
                        setCurrentIndex(
                          Math.min(previewList.length - 1, currentIndex + 1),
                        )
                      }
                      disabled={currentIndex === previewList.length - 1}
                    >
                      <i className="ri-arrow-right-s-line fs-5" />
                    </Button>
                  </div>
                </div>
              )}
              {hasPermission(PERMISSION_MODULES.RIAVIAL_LABEL, PERMISSION_ACTIONS.PRINT) && (
                <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                  <span className="text-muted small fw-semibold">Quantity</span>
                  <div className="d-flex align-items-center gap-3">
                    <Button
                      size="sm"
                      color="light"
                      className="rounded-circle p-0 d-flex align-items-center justify-content-center border shadow-sm"
                      style={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() => {
                        if (!currentSubject) return;
                        const id = currentSubject.sampleseparationid;
                        setIndividualCounts((prev) => ({
                          ...prev,
                          [id]: Math.max(1, getCountForId(id) - 1),
                        }));
                      }}
                      disabled={
                        !currentSubject ||
                        (currentSubject &&
                          getCountForId(currentSubject.sampleseparationid) <= 1)
                      }
                    >
                      <i className="ri-subtract-line fs-6" />
                    </Button>
                    <span
                      className="fw-bold text-primary"
                      style={{ minWidth: "20px", textAlign: "center" }}
                    >
                      {currentSubject
                        ? getCountForId(currentSubject.sampleseparationid)
                        : 0}
                    </span>
                    <Button
                      size="sm"
                      color="light"
                      className="rounded-circle p-0 d-flex align-items-center justify-content-center border shadow-sm"
                      style={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#fff",
                      }}
                      onClick={() => {
                        if (!currentSubject) return;
                        const id = currentSubject.sampleseparationid;
                        setIndividualCounts((prev) => ({
                          ...prev,
                          [id]: getCountForId(id) + 1,
                        }));
                      }}
                      disabled={!currentSubject}
                    >
                      <i className="ri-add-line fs-6" />
                    </Button>
                  </div>
                </div>
              )}

              <CommonButton
                module={PERMISSION_MODULES.RIAVIAL_LABEL}
                action={PERMISSION_ACTIONS.PRINT}
                color="primary"
                block
                className="py-2 rounded-3 d-flex align-items-center justify-content-center gap-2 fw-700 shadow-sm mt-auto"
                disabled={
                  previewList.length === 0 ||
                  UpdateCryovialLabelMutation.isPending
                }
                onClick={handlePrint}
              >
                {UpdateCryovialLabelMutation.isPending ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="ri-printer-line" />
                )}{" "}
                Print Labels
              </CommonButton>
            </CardBody>
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default CryovialLabelList;
